import React, { useState } from "react";
import { ChevronDown } from "lucide-react";
import { FILTER_DEFS, FILTER_IDS } from "./rosterTagDefs";

/**
 * Expanded-panel presentation: chips inline, no popover.
 *
 * At 1120px the roster pane has room to show the filters without hiding them
 * behind a trigger, so it does. Zero-count chips still collapse — but behind a
 * "+N empty" disclosure rather than a dimmed row, because the rail is a single
 * line and a second dimmed line would undo the saving.
 */

/** The rail is width-constrained, so the stale chips drop the word "plan". */
const SHORT_LABELS = {
  [FILTER_IDS.DIET_STALE]: (days) => `Diet >${days}d`,
  [FILTER_IDS.WORKOUT_STALE]: (days) => `Workout >${days}d`,
};

const railLabel = (id, thresholdDays) =>
  SHORT_LABELS[id] ? SHORT_LABELS[id](thresholdDays) : FILTER_DEFS[id]?.label || id;

/**
 * @param {Object} props
 * @param {Set<string>} props.activeIds
 * @param {(id: string|null) => void} props.onToggle
 * @param {Record<string, number>} props.counts
 * @param {string[]} props.chipIds
 * @param {number} props.thresholdDays
 */
export function InlineFilterRail({ activeIds, onToggle, counts, chipIds, thresholdDays }) {
  const [showEmpty, setShowEmpty] = useState(false);

  const live = chipIds.filter((id) => counts[id] > 0 || activeIds.has(id));
  const empty = chipIds.filter((id) => counts[id] === 0 && !activeIds.has(id));
  const visible = showEmpty ? [...live, ...empty] : live;

  return (
    <div className="flex flex-wrap items-center gap-[7px] px-3.5 py-3">
      {visible.map((id) => {
        const active = activeIds.has(id);
        return (
          <button
            key={id}
            type="button"
            onClick={() => onToggle(id)}
            aria-pressed={active}
            className={`inline-flex items-center gap-1.5 rounded-full border px-[11px] py-1.5 text-xs transition-colors ${
              active
                ? "border-orange-primary bg-light-orange font-bold text-bc-accent-text"
                : "border-gray-200 bg-white font-semibold text-gray-700 hover:bg-orange-50"
            }`}
          >
            <span>{railLabel(id, thresholdDays)}</span>
            <span className={active ? "font-semibold opacity-60" : "text-gray-400"}>
              {counts[id]}
            </span>
          </button>
        );
      })}

      {empty.length > 0 && !showEmpty && (
        <button
          type="button"
          onClick={() => setShowEmpty(true)}
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-[#FBFBFB] px-2.5 py-1.5 text-xs font-semibold text-gray-400 transition-colors hover:bg-gray-50"
        >
          +{empty.length} empty
          <ChevronDown aria-hidden="true" className="h-3 w-3" />
        </button>
      )}
    </div>
  );
}

export default InlineFilterRail;
